import {
  AfterViewInit,
  Component,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { ReadingQuestionAnswer } from '../../../../models/answer/reading-answer.model';
import { ReadingQuestion } from '../../../../models/questions/reading-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { QuestionControlType } from '../question-type.interface';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import { IonCheckbox } from '@ionic/angular/standalone';

/**
 * The question type that only contains text to read. It does contain a field
 * to indicate that , but it isn't shown to the user.
 *
 * <lib-reading-question
 *                       [formControl]="questionFormCtrl">
 * </lib-reading-question>
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-reading-question',
  templateUrl: './reading-question.component.html',
  styleUrls: ['./reading-question.component.scss'],
  imports: [QuestionDisplayComponent, IonCheckbox, ReactiveFormsModule],
})
export class ReadingQuestionComponent
  implements AfterViewInit, OnInit, OnLoaded, QuestionControlType
{
  /**
   * The question that is presented.
   */
  public question!: ReadingQuestion;

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: ReadingQuestionAnswer | undefined) {
    this.checkboxCtrl.setValue(answer);
  }

  public get answer(): ReadingQuestionAnswer | undefined {
    return this.getValue();
  }

  /**
   * The form control ion-checkbox component
   */
  public checkboxCtrl: UntypedFormControl = new UntypedFormControl(
    null,
    Validators.requiredTrue,
  );

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  public ngOnInit(): void {
    // Always set to true, as the user only has to read the text and the
    // checkbox isn't displayed.
    this.answer = true;
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.checkboxCtrl.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): ReadingQuestionAnswer {
    const value: boolean = this.checkboxCtrl.value;
    if (typeof value === 'boolean') {
      return value;
    }
    return null as unknown as ReadingQuestionAnswer;
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<ReadingQuestionAnswer> {
    return this.checkboxCtrl.valueChanges.pipe(
      map((value) => {
        if (typeof value === 'boolean') {
          return value;
        }
        return null as unknown as ReadingQuestionAnswer;
      }),
    );
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.checkboxCtrl.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.checkboxCtrl.statusChanges.pipe(
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.checkboxCtrl.disable();
    } else {
      this.checkboxCtrl.enable();
    }
  }
}
